import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools'; // Add this for development
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import PrivateRoute from './components/PrivateRoute';

// Layouts
import MainLayout from './layouts/MainLayout';
import AuthLayout from './layouts/AuthLayout';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import ShopOwners from './pages/ShopOwners';
import Consignments from './pages/Consignments';
import CreateConsignment from './pages/CreateConsignment';
import ConsignmentDetails from './pages/ConsignmentDetails';
import Sales from './pages/Sales';
import CreateSale from './pages/CreateSale';
import SaleDetails from './pages/SaleDetails';
import Payments from './pages/Payments';
import CreatePayment from './pages/CreatePayment';
import Reports from './pages/Reports';
import Expenses from './pages/Expenses';
import CreateExpense from './pages/CreateExpense';
import Inventory from './pages/Inventory';
import CreateProduct from './pages/CreateProduct';
import ProductDetails from './pages/ProductDetails';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import Notifications from './pages/Notifications';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Unauthorized from './pages/Unauthorized';
import NotFound from './pages/NotFound';
import Depots from './pages/Depots';
import Transfers from './pages/Transfers';
import Orders from './pages/Orders';

// Styles
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <Routes>
            {/* Auth Routes */}
            <Route path="/auth" element={<AuthLayout />}>
              <Route index element={<Navigate to="login" replace />} />
              <Route path="login" element={<Login />} />
              <Route path="forgot-password" element={<ForgotPassword />} />
              <Route path="reset-password" element={<ResetPassword />} />
            </Route>
            
            {/* Protected Routes */}
            <Route path="/" element={<PrivateRoute><MainLayout /></PrivateRoute>}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              
              {/* Core Routes */}
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="users" element={<Users />} />
              <Route path="shop-owners" element={<ShopOwners />} />
              
              {/* Consignment Routes */}
              <Route path="consignments" element={<Consignments />} />
              <Route path="consignments/create" element={<CreateConsignment />} />
              <Route path="consignments/:id" element={<ConsignmentDetails />} />
              
              {/* Sales Routes */}
              <Route path="sales" element={<Sales />} />
              <Route path="sales/create" element={<CreateSale />} />
              <Route path="sales/:id" element={<SaleDetails />} />
              
              {/* Payment Routes */}
              <Route path="payments" element={<Payments />} />
              <Route path="payments/create" element={<CreatePayment />} />
              <Route path="payments/transaction/:transactionId" element={<CreatePayment />} />
              
              {/* Report Routes */}
              <Route path="reports" element={<Reports />} />
              
              {/* Expense Routes */}
              <Route path="expenses" element={<Expenses />} />
              <Route path="expenses/create" element={<CreateExpense />} />
              
              {/* Inventory Routes */}
              <Route path="inventory" element={<Inventory />} />
              <Route path="inventory/create-product" element={<CreateProduct />} />
              <Route path="inventory/product/:id" element={<ProductDetails />} />

              {/* Depots Routes*/}
              <Route path="depots" element={<Depots />} /> 
              
              {/* Stock Transfers Routes*/}
              <Route path="transfers" element={<Transfers />} />
              
              {/* Orders Routes */}
              <Route path="orders" element={<Orders />} />

              {/* User Routes */}
              <Route path="settings" element={<Settings />} />
              <Route path="profile" element={<Profile />} />
              <Route path="notifications" element={<Notifications />} />
            </Route>
            
            {/* Public Routes */}
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route path="/404" element={<NotFound />} />
            <Route path="*" element={<Navigate to="/404" replace />} />
          </Routes>
        </Router>
        
        {/* Toast notifications */}
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#10b981',
                secondary: '#fff',
              },
            },
            error: {
              duration: 4000,
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
            loading: {
              duration: Infinity,
              iconTheme: {
                primary: '#3b82f6',
                secondary: '#fff',
              },
            },
          }}
        />
      </AuthProvider>
      
      {/* React Query Devtools - only in development */}
      {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

export default App;